import { useTranslation } from 'react-i18next';
import React, { useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { NavContext } from '@/components/NavContext';
import { useMediaQuery } from 'react-responsive';
import { AuthContext } from '@/components/Contexts/AuthContext';
import {
  IconButton,
  Header,
  Dropdown,
  Button,
  Tag,
  PersonAvatar,
  Avatar,
  AvatarStack,
  TEOLoader,
} from '@teo/components';
import {
  MoreHorizontal,
  CrossFilled,
  Options,
  DuoToneTrajectoryFilled,
  Share,
  DuoToneAccountFilled,
} from '@teo/components/icons';
import { nthLastIndexOf } from '@/util';
import { axios } from '@/axios';
import { config } from '@/config';
import { format } from 'date-fns';
import clsx from 'clsx';
import i18n from 'i18next';
import ComponentDeleteModal from '@/components/DeleteModal/ComponentDeleteModal';

function LearningPathHeader({
  learningpathId,
  learningpath,
  setLearningpath,
  setIsOpenPublish,
  setIsOpenVersionModal,
  contentCreators,
  setIsOpenInviteModal,
  isOwner,
  authOrg,
  setIsMisingConnect,
  setMisingConnect,
}: any) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { auth } = useContext(AuthContext);
  const authUser = auth?.user;
  const { setNavDisabled } = useContext(NavContext);

  const [preloader, setPreloader] = useState(false);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);

  const isDesktop = useMediaQuery({
    query: '(min-width: 1280px)',
  });

  const isMobile = useMediaQuery({
    query: '(max-width: 767px)',
  });

  const basePath = window.location.pathname.slice(
    0,
    nthLastIndexOf(window.location.pathname, '/', 1)
  );

  const handleDeleteLP = () => {
    setIsOpenDeleteModal(true);
  };

  const handleDeleteLearningPath = () => {
    axios
      .post(`${config.backend}/learningpath/detach/${learningpathId}`)
      .then(() => navigate(basePath));
  };

  const handleDeactivate = async () => {
    await axios
      .get(`${config.backend}/memberlpgroupdetails?masterId=${learningpathId}`)
      .then((res) => {
        const allPublicationLp = res?.data || [];
        const promises: any = [];

        allPublicationLp.map((el: any) => {
          promises.push(
            axios.put(`${config.backend}/lpgroup/${el?.lpgroupId}`, {
              lpStatus: 'disabled',
            })
          );
        });
        Promise.allSettled(promises).then(async () => {
          setLearningpath((prev: any) => ({
            ...prev,
            lpPublicationStatus: 'disabled',
          }));
          await axios.put(`${config.backend}/learningpath/${learningpathId}`, {
            lpPublicationStatus: 'disabled',
          });
        });
      });
  };

  const handleActivate = async () => {
    setLearningpath((prev: any) => ({
      ...prev,
      lpPublicationStatus: 'concept',
    }));
    await axios.put(`${config.backend}/learningpath/${learningpathId}`, {
      lpPublicationStatus: 'concept',
    });
  };

  const handleDuplicate = async () => {
    const data = await axios.post(
      `${config.backend}/learningpath/copy/${learningpathId}`,
      { organisationId: authUser?.activeOrganisationId }
    );
    await axios.put(`${config.backend}/learningpath/${data?.data?.id}`, {
      lpPublicationStatus: 'concept',
      masterId: null,
      lpName:
        learningpath?.lpName + ` (${t('pages.create_edit_course.duplicate')})`,
    });
    await navigate(
      `/${i18n.language}/create/${authUser?.activeOrganisationId}/learning-paths/${data?.data?.id}`
    );
    navigate(0);
  };

  function findDisconnectedNodes(nodes: any) {
    const nodeById = new Map(nodes.map((n: any) => [n.id, n]));
    const rootNode = nodes.find((n: any) => n.type === 'root');

    if (!rootNode) {
      throw new Error('Root element not found');
    }

    const connectedCache = new Map();

    function isConnectedToRoot(node: any) {
      if (node.id === rootNode.id) return true;
      if (connectedCache.has(node.id)) {
        return connectedCache.get(node.id);
      }

      const result =
        node.roots?.some((parentId: any) => {
          const parent = nodeById.get(parentId);
          return parent ? isConnectedToRoot(parent) : false;
        }) ?? false;

      connectedCache.set(node.id, result);
      return result;
    }

    return nodes.filter((node: any) => !isConnectedToRoot(node));
  }

  const handlePublish = async () => {
    const result = await axios.get(
      `${config.backend}/learningpathnodedetails?learningpathId=${learningpathId}&organisationId=${authOrg}`
    );
    if (result?.data?.length > 0) {
      const disconnected = findDisconnectedNodes(result?.data);
      if (disconnected?.length > 0) {
        setMisingConnect(disconnected);
        setIsMisingConnect(true);
      } else {
        setMisingConnect(null);
        setIsOpenPublish(true);
      }
    }
  };

  const PrsonUpadteComponent = () => {
    const avatars = [];
    avatars.push(
      ...(contentCreators || [])
        .map((item: any) => (
          <PersonAvatar
            key={`user_${item.id}`}
            name={item.username || ''}
            imageSrc={item?.picture ? item?.picture : ''}
            id={item.id}
            //key={item.id}
          />
        ))
        .slice(0, 3)
    );
    avatars.push(
      <Avatar key="icon_ket" className={clsx(' bg-grey-02 text-black')}>
        <div className="flex h-8 min-h-8 w-8 min-w-8 items-center justify-center">
          <Share className=""></Share>
        </div>
      </Avatar>
    );

    return (
      <>
        <Button
          size="sm"
          variant="ghost"
          className={`relative text-xs font-normal !text-[#919193] xl:text-center `}
          onClick={() => setIsOpenVersionModal(true)}
        >
          {t('pages.create_edit_course.latest_update')}
          {format(new Date(learningpath.updatedAt || ''), 'dd/MM/yyyy')}
        </Button>
        <div className={`group relative mt-4 xl:mx-6 xl:mt-0 `}>
          {!isOwner && (
            <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
              {t('pages.create_edit_course.message_not_publish')}
            </div>
          )}
          {contentCreators?.length > 0 ? (
            <div
              role="button"
              className={`rounded-lg p-1 pl-1 ${
                !isOwner && 'pointer-events-none'
              }`}
            >
              <AvatarStack
                maxAvatars={4}
                onClick={() => {
                  setIsOpenInviteModal(true);
                }}
              >
                {avatars}
              </AvatarStack>
            </div>
          ) : (
            <IconButton
              Icon={DuoToneAccountFilled}
              ariaLabel="Search"
              className="!rounded-full"
              variant="outline"
              onClick={() => {
                setIsOpenInviteModal(true);
              }}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <div>
      {preloader && (
        <div className="fixed inset-0 z-[11] flex bg-white/75">
          <div className="m-auto">
            <TEOLoader
              loaderSrc="/images/TEOloader.gif"
              className="w-[110px]"
            />
          </div>
        </div>
      )}
      {!isMobile ? (
        <Header
          imgSrc="/images/TEO_logo.svg"
          navigation={
            <div className="flex items-center">
              <IconButton
                className="my-auto mx-2"
                Icon={CrossFilled}
                variant="ghost"
                onClick={async () => navigate(basePath)}
              />
              <DuoToneTrajectoryFilled className="h-6 w-6 text-[#7d7d81]" />

              <div className="mx-5 text-base font-medium line-clamp-2">
                {learningpath?.lpName || t('pages.learning_path.untitled_name')}
              </div>

              <div className="mr-4 lg:mr-0">
                <Tag
                  title={learningpath?.lpPublicationStatus || ''}
                  size="md"
                  variant={
                    learningpath?.lpPublicationStatus === 'published'
                      ? 'primary'
                      : 'default'
                  }
                />
              </div>
            </div>
          }
          rightComponent={
            <div className="flex items-center" data-testid="handleSettings">
              {isDesktop && <PrsonUpadteComponent />}
              {/* <div className="relative mr-3">
                <Dropdown>
                  <Dropdown.Trigger>
                    <p className="rounded-md border border-grey-03 px-4 py-2 text-center text-sm font-medium text-grey-09 transition-colors duration-200 ease-out hover:bg-grey-transparent-01">
                      {t('pages.create_edit_course.example_button')}
                    </p>
                  </Dropdown.Trigger>
                  <Dropdown.Content>
                    <Dropdown.Actions>
                      <Dropdown.Button
                        onClick={async () => {}}
                        className="disabled:pointer-events-none disabled:text-grey-04"
                      >
                        <p>{t('pages.create_edit_course.view_mobile')}</p>
                      </Dropdown.Button>

                      <Dropdown.Button
                        onClick={async () => {}}
                        className="disabled:pointer-events-none disabled:text-grey-04"
                      >
                        <p>{t('pages.create_edit_course.view_desktop')}</p>
                      </Dropdown.Button>
                    </Dropdown.Actions>
                  </Dropdown.Content>
                </Dropdown>
              </div> */}
              <div className={`group relative`}>
                <Button
                  disabled={
                    (!isOwner && learningpath?.lpAccess !== 'protected') ||
                    learningpath?.lpPublicationStatus === 'disabled'
                  }
                  onClick={async () => handlePublish()}
                  size="md"
                >
                  {t('pages.create_edit_course.publish_button')}
                </Button>
              </div>
              <div className="relative z-[11] ml-3">
                <Dropdown>
                  <div data-testid="handleOptions">
                    <Dropdown.Trigger className="handleOptions">
                      <span className="inline-flex rounded-md p-2 hover:bg-grey-transparent-01">
                        <MoreHorizontal
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </span>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                      {!isDesktop && (
                        <Dropdown.Section>
                          <PrsonUpadteComponent />
                        </Dropdown.Section>
                      )}
                      <Dropdown.Actions>
                        <Dropdown.Button
                          onClick={() => {
                            setPreloader(true);
                            handleDuplicate();
                          }}
                        >
                          {t('pages.create_edit_course.duplicate_button')}
                        </Dropdown.Button>

                        {learningpath?.lpPublicationStatus === 'disabled' ? (
                          <Dropdown.Button
                            onClick={() => isOwner && handleActivate()}
                            disabled={!isOwner}
                            className={`group relative ${
                              !isOwner && 'text-grey-04'
                            }`}
                          >
                            {t('pages.create_edit_course.activate_button')}
                            {!isOwner && (
                              <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                                {t(
                                  'pages.create_edit_course.message_not_owner'
                                )}
                              </div>
                            )}
                          </Dropdown.Button>
                        ) : (
                          <Dropdown.Button
                            onClick={() => isOwner && handleDeactivate()}
                            disabled={!isOwner}
                            className={`group relative ${
                              !isOwner && 'text-grey-04'
                            }`}
                          >
                            {t('pages.create_edit_course.deactivate_button')}
                            {!isOwner && (
                              <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                                {t(
                                  'pages.create_edit_course.message_not_owner'
                                )}
                              </div>
                            )}
                          </Dropdown.Button>
                        )}

                        <Dropdown.Button
                          onClick={() => isOwner && handleDeleteLP()}
                          disabled={!isOwner}
                          className={`group relative text-[#FC6232] ${
                            !isOwner && ' text-grey-04'
                          }`}
                        >
                          {t('pages.create_edit_course.delete_button')}
                          {!isOwner && (
                            <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                              {t('pages.create_edit_course.message_not_owner')}
                            </div>
                          )}
                        </Dropdown.Button>
                      </Dropdown.Actions>
                    </Dropdown.Content>
                  </div>
                </Dropdown>
              </div>
            </div>
          }
        ></Header>
      ) : (
        <header
          className={`flex flex-col border-b border-solid border-grey-02 bg-white py-3`}
        >
          <div className="flex items-center justify-start px-3">
            <img src="/images/TEO_logo.svg" className="mr-4" />
            <nav className="w-full">
              <div className="flex items-center">
                <IconButton
                  className="my-auto mr-auto"
                  Icon={CrossFilled}
                  variant="ghost"
                  onClick={async () => navigate(basePath)}
                />
                {/* <div className="mr-3">
                  <Button onClick={async () => {}} size="sm" variant="outline">
                    {t('pages.create_edit_course.example_button')}
                  </Button>
                </div> */}
                <div className="group relative">
                  <Button
                    onClick={() => handlePublish()}
                    disabled={
                      (!isOwner && learningpath?.lpAccess !== 'protected') ||
                      learningpath?.lpPublicationStatus === 'disabled'
                    }
                    size="sm"
                  >
                    {t('pages.create_edit_course.publish_button')}
                  </Button>
                  {!isOwner && learningpath?.lpAccess !== 'protected' && (
                    <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                      {t('pages.create_edit_course.message_not_publish')}
                    </div>
                  )}
                </div>
                <div className="ml-3">
                  <Dropdown>
                    <Dropdown.Trigger>
                      <span className="inline-flex rounded-md p-2 hover:bg-grey-transparent-01">
                        <MoreHorizontal
                          className="h-5 w-5"
                          aria-hidden="true"
                        />
                      </span>
                    </Dropdown.Trigger>
                    <Dropdown.Content>
                      {!isDesktop && (
                        <Dropdown.Section>
                          <PrsonUpadteComponent />
                        </Dropdown.Section>
                      )}

                      <Dropdown.Button onClick={() => handleDuplicate()}>
                        {t('pages.create_edit_course.duplicate_button')}
                      </Dropdown.Button>

                      {learningpath?.lpPublicationStatus === 'disabled' ? (
                        <Dropdown.Button
                          onClick={() => isOwner && handleActivate()}
                          disabled={!isOwner}
                          className={`group relative ${
                            !isOwner && 'text-grey-04'
                          }`}
                        >
                          {t('pages.create_edit_course.activate_button')}
                          {!isOwner && (
                            <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                              {t('pages.create_edit_course.message_not_owner')}
                            </div>
                          )}
                        </Dropdown.Button>
                      ) : (
                        <Dropdown.Button
                          onClick={() => isOwner && handleDeactivate()}
                          disabled={!isOwner}
                          className={`group relative ${
                            !isOwner && 'text-grey-04'
                          }`}
                        >
                          {t('pages.create_edit_course.deactivate_button')}
                          {!isOwner && (
                            <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                              {t('pages.create_edit_course.message_not_owner')}
                            </div>
                          )}
                        </Dropdown.Button>
                      )}

                      <Dropdown.Button
                        onClick={() => isOwner && handleDeleteLP()}
                        disabled={!isOwner}
                        className={`group relative text-[#FC6232] ${
                          !isOwner && ' text-grey-04'
                        }`}
                      >
                        {t('pages.create_edit_course.delete_button')}
                        {!isOwner && (
                          <div className="absolute right-0 top-[110%] z-[10] hidden w-[300px] rounded-lg bg-white p-3 text-xs text-grey-08 shadow-[0_0_5px_-1px_rgba(0,0,0,0.3)] group-hover:flex">
                            {t('pages.create_edit_course.message_not_owner')}
                          </div>
                        )}
                      </Dropdown.Button>
                      {/* </Dropdown.Actions> */}
                    </Dropdown.Content>
                  </Dropdown>
                </div>
              </div>
            </nav>
          </div>
          <div className="mt-4 flex items-center border-t border-solid border-grey-02 px-3 pt-3">
            <div className="text-base font-medium line-clamp-2">
              {t('pages.learning_path.untitled_name')}
            </div>

            <div className="mx-2">
              <Tag
                title={learningpath?.lpPublicationStatus || ''}
                variant={
                  learningpath.lpPublicationStatus === 'published'
                    ? 'primary'
                    : 'default'
                }
                size="sm"
              />
            </div>
          </div>
        </header>
      )}

      {isOpenDeleteModal && (
        <ComponentDeleteModal
          closeModal={() => setIsOpenDeleteModal(false)}
          setIsOpenDeleteModal={setIsOpenDeleteModal}
          title={t('delete_modal.title_lp_c')}
          heading={
            <>
              <p>{t('delete_modal.heading_lp_c')}</p>
              <br />
              <p>{t('delete_modal.heading_lp_warn')}</p>
            </>
          }
          handleDelete={() => handleDeleteLearningPath()}
        />
      )}
    </div>
  );
}

export default LearningPathHeader;
